/**
 * SocialMediaAdsService
 * Service for social media advertising operations
 * Route: api/socialmedia/ads
 */
import { BaseService } from '../base/BaseService';
/**
 * SocialMediaAdsService
 * Handles social media advertising campaigns, ad sets, and ads
 */
export class SocialMediaAdsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SocialMediaAdsService.DEFAULT_SERVICE_PATH,
            basePath: SocialMediaAdsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CAMPAIGNS ====================
    /**
     * Get campaign by ID
     * GET api/socialmedia/ads/campaigns/{id}
     */
    async getCampaignById(id) {
        const response = await this.getCampaignByIdRaw(id);
        if (!response.data) {
            throw new Error(`Campaign ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get campaign by ID with full response
     */
    getCampaignByIdRaw(id) {
        const endpoint = this.getEndpoint('campaign', `campaigns/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get campaigns by account ID
     * GET api/socialmedia/ads/campaigns/account/{accountId}
     */
    async getCampaignsByAccount(accountId) {
        const response = await this.getCampaignsByAccountRaw(accountId);
        return response.data ?? [];
    }
    /**
     * Get campaigns by account ID with full response
     */
    getCampaignsByAccountRaw(accountId) {
        const endpoint = this.getEndpoint('campaigns-by-account', `campaigns/account/${accountId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== AD SETS ====================
    /**
     * Get ad set by ID
     * GET api/socialmedia/ads/adsets/{id}
     */
    async getAdSetById(id) {
        const response = await this.getAdSetByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ad set ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ad set by ID with full response
     */
    getAdSetByIdRaw(id) {
        const endpoint = this.getEndpoint('adset', `adsets/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get ad sets by campaign ID
     * GET api/socialmedia/ads/adsets/campaign/{campaignId}
     */
    async getAdSetsByCampaign(campaignId) {
        const response = await this.getAdSetsByCampaignRaw(campaignId);
        return response.data ?? [];
    }
    /**
     * Get ad sets by campaign ID with full response
     */
    getAdSetsByCampaignRaw(campaignId) {
        const endpoint = this.getEndpoint('adsets-by-campaign', `adsets/campaign/${campaignId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== ADS ====================
    /**
     * Get ad by ID
     * GET api/socialmedia/ads/{id}
     */
    async getAdById(id) {
        const response = await this.getAdByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ad ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ad by ID with full response
     */
    getAdByIdRaw(id) {
        const endpoint = this.getEndpoint('ad', id);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get ads by ad set ID
     * GET api/socialmedia/ads/adset/{adSetId}
     */
    async getAdsByAdSet(adSetId) {
        const response = await this.getAdsByAdSetRaw(adSetId);
        return response.data ?? [];
    }
    /**
     * Get ads by ad set ID with full response
     */
    getAdsByAdSetRaw(adSetId) {
        const endpoint = this.getEndpoint('ads-by-adset', `adset/${adSetId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
SocialMediaAdsService.DEFAULT_SERVICE_PATH = 'social-media';
SocialMediaAdsService.BASE_PATH = 'api/socialmedia/ads';
//# sourceMappingURL=SocialMediaAdsService.js.map